/**
 * English parse/lexer/decl-parser diagnostic templates (parse.*), split out
 * from catalog.en.ts because there are ~140 of them. Same ICU rules: literal
 * `{ }` braces go in params (open/close/frag/example/block), apostrophes are
 * auto-doubled, `<…>` is literal. Merged into the catalog by catalog.ts;
 * catalog.parse.ru.ts mirrors this key-for-key.
 */

export const DIAG_PARSE_EN: Record<string, string> = {
  // parser.ts — token/structure expectations (the `got '{got}'` suffix is
  // appended by the consume() helper).
  "parse.expectedAfterGot": "Expected {expected} after {after} (got '{got}')",
  "parse.expectedThingGot": "Expected {thing} (got '{got}')",
  "parse.expectedOpenAfterGot": "Expected {open} after {after} (got '{got}')",
  "parse.expectedOpenGot": "Expected {open} (got '{got}')",
  "parse.expectedColonOrOpenForGot": "Expected ':' or {open} after 'for' (got '{got}')",
  "parse.expectedCloseToGot": "Expected {close} to close {what} (got '{got}')",
  "parse.expectedCloseAfterGot": "Expected {close} after {what} (got '{got}')",
  "parse.expectedCloseBracketGot": "Expected ']' to close list (got '{got}')",
  "parse.expectedOpenToOpenGot": "Expected {open} to open '{context}' body (got '{got}')",
  "parse.expectedCloseToCloseGot": "Expected {close} to close '{context}' body (got '{got}')",
  "parse.expectedValueGot": "Expected value, got {got}",
  "parse.duplicateManifest": "Duplicate {what} in package manifest",
  "parse.duplicateDependency": "Duplicate dependency '{name}' in package manifest",
  "parse.manifestBothPackageAndName":
    "Package manifest declares both 'package' and 'name'; a manifest is either a package root ('package:') or a space ('name:'), not both",
  "parse.manifestNeitherPackageNorName":
    "Package manifest must declare 'package' (a package root) or 'name' (a space)",
  "parse.unexpectedTokenIn": "Unexpected token in {context}: {value}",
  "parse.expectedPlaneAfterOn": "Expected a plane name after 'on'",
  "parse.expectedPlaneNameOn": "Expected plane name after 'on'",
  "parse.expectedDeclOrOpenAfterOn": "Expected a declaration or {open} after 'on <plane>'",
  "parse.planeListOneLine": "A plane list must be complete on one line — remove the trailing ','",
  "parse.declCannotBePlaneScoped":
    "A {kind} declaration cannot be plane-scoped — 'on' applies to modules, processes, and subprocesses",
  "parse.conflictingPlacementInClause":
    "conflicting placement — the declaration already has an 'in' clause; use a single 'in <Target>' prefix",
  "parse.exportInPlacedProcess":
    "'export' applies to top-level process declarations only — an 'in'-placed process is scoped under its target module and takes no 'export'",
  "parse.inCannotPlaceQuery":
    "'in <Target>' cannot place a query — declare it at top level or inside the module body",
  "parse.inCannotPlaceLens":
    "'in <Target>' cannot place a lens — declare it at top level or inside the module body",
  "parse.inPlaceableKinds":
    "'in <Target>' may place a module, a process, a view, or a policy — not an interface or surface",
  "parse.inExpectsDeclOrBlock": "'in <Target>' expects a declaration or a {frag} block",
  "parse.sepBetweenNewline": "Expected ';' or newline between {noun}",
  "parse.sepBetweenComma": "Expected ';' or newline between {noun} — ',' separates list values",
  "parse.typeSubtypingReserved":
    "'type {parentType}' is not allowed; {parentType} subtyping is reserved for future iterations",
  "parse.suffixInRemoved": "suffix 'in <Parent>' is removed — write 'in <Parent> <decl>' (§4.16)",
  "parse.aspectsHeaderOnly": "Aspects must be declared in the process header, before the first step",
  "parse.fieldBodiesProcess": "Field bodies are not allowed on a process — use scalar or list values",
  "parse.unwindOnlyReversible":
    "'unwind' may only appear in a reversible block, not inside control flow — wrap the control in a block and attach 'unwind' to the block",
  "parse.expectedAfterDitto": "Expected a call, note, block, or control construct after '|' (fan owner)",
  "parse.snakeRequiresCall": "'\\' (snake) continues a run of CALLS — write '\\ > Callee'. It names an edge's caller, so it cannot prefix a note, block, control construct, 'do', 'go', or an 'unwind' action",
  "parse.distControlOnly":
    "'dist' may only prefix a control construct or an owner block (control is emergent; a call always has a concrete caller)",
  "parse.anonProcessCallsOnly":
    "An anonymous process supports calls only — wrap branching, control flow, notes, and terminators in a named 'process Name { }'",
  "parse.decorationOnUse":
    "A decoration cannot annotate a 'use' import — an import resolves to no model element; decorate the element where it is declared, in the package it comes from",
  "parse.expectedControlAfterOwner": "Expected a control construct after owner prefix",
  "parse.fieldBodiesNote": "Field bodies are not allowed in a note body — use scalar or list values",
  "parse.noteBodyFieldsOnly": "A note body holds field assignments only (e.g. latency: 1d-3d)",
  "parse.expectedGtStep": "Expected '>' in process step",
  "parse.caseLabelBeforeProse": "Expected a case label before prose in a select body",
  "parse.expectedCaseLabel": "Expected case label (identifier or string) in select body",
  "parse.awaitNoJoinRace": "'await' cannot combine with 'join'/'race' — the await-clause implies race semantics",
  "parse.eachSelectNoAwait": "'each'/'select' cannot compose with an await-clause (v0.9.0)",
  "parse.awaitSingleBranch":
    "An await-clause takes a single implicit branch — 'branch' bodies cannot appear under it (v0.9.0 §2.2)",
  "parse.parallelBranchStart":
    "Parallel branch must start with 'branch' (then ':' one-liner, {open} block, or a control construct)",
  "parse.expectedTriggerAfterOn": "Expected trigger (identifier or string) after 'on'",
  "parse.unwindAttachSimple":
    "'unwind' may only attach to a simple step or a block, not a control construct — wrap the control in a block and attach 'unwind' to the block",
  "parse.unwindActionKinds":
    "unwind action must be a call ('>'), 'fail'/'finish', 'do', or a block — a bare interface has no caller",
  "parse.expectedActionAfterUnwind": "Expected an action after 'unwind'",
  "parse.expectedAwaitTarget": "Expected await target (string or words)",
  "parse.partialPresenceOnePlane": "Partial presence takes ONE plane per block — split into several {frag} blocks",
  "parse.partialPresenceNoAspects":
    "A partial-presence {frag} block can't contain aspects — declare aspects on the module body",
  "parse.labelsRenamedAspectValue": "'labels' was renamed to 'aspect' (v0.9.0) — write {frag}",
  "parse.inPlacementTopLevelOnly":
    "placement 'in <Target>' is a top-level statement — it can't nest inside a body (§4.16); nest the declaration lexically instead",
  "parse.exportModuleScopedProcess":
    "'export' applies to top-level process declarations only — a module-scoped process is not exportable (a space's published surface is declared at its root)",
  "parse.exportOnPresenceInBody": "'export' cannot prefix a presence form in a body — mark the members instead: on <plane> { export <decl> }",
  "parse.exportInterfaceOrSubprocess": "'export' may only prefix a module, interface, or subprocess",
  "parse.aspectNoDot": "No dot after 'aspect' — write {frag}",
  "parse.rosterTrailingComma": "Trailing ',' in a roster — remove it or add a module reference",
  "parse.aspectNoSubFieldBodies":
    "Aspects do not support sub-field bodies — use a separate {frag} block for nested keys",
  "parse.aspectNoSubFieldBodiesShort": "Aspects do not support sub-field bodies",
  "parse.rosterEntryModuleRef":
    "A roster entry must be a module reference — implicit (string) places cannot be members",
  "parse.expectedRosterModuleRef": "Expected a module reference in the roster",
  "parse.bareListOneLine":
    "Trailing ',' — a bare list must fit on one line; use brackets [ ... ] for multi-line lists",
  "parse.expectedValueAfterComma": "Expected a value after ','",
  "parse.expectedTypeAfterOverride": "Expected type identifier after 'override'",
  "parse.duplicateModifier": "Duplicate '{modifier}' modifier",
  "parse.cascadeAppendExclusive": "'cascade' and 'append' are mutually exclusive",
  "parse.groupMarkerNeedsCascade": "'*' group marker requires 'cascade'",
  "parse.aspectNoCascadeReason":
    "Cascade/append modifiers cannot be applied to aspects — they always cascade (§4.4)",
  "parse.labelsRenamedAspectRequired": "'labels' was renamed to 'aspect' (v0.9.0) — write {frag}",
  "parse.aspectNoCascade": "Cascade/append modifiers cannot be applied to aspects",
  "parse.expectedFieldOrSubDecl": "Expected field name or sub-declaration after modifiers",
  "parse.fieldNeedsValue":
    "Field declaration must have a value, body, 'required', or a cascade/append modifier",
  "parse.requiredFieldNoValue": "'required' field cannot have a value (mandatory blanks have no value)",
  "parse.cascadeAppendFieldsOnly": "'cascade'/'append' apply only to fields, not sub-declarations",
  "parse.expectedOpenFieldBody": "Expected {open} to begin field body",
  "parse.subFieldCascadeImplicit":
    "Sub-fields under a 'cascade *' group inherit cascade implicitly — do not repeat 'cascade'",
  "parse.expectedSubFieldName": "Expected sub-field name inside field body",
  "parse.subFieldNeedsValue":
    "Sub-field declaration must have a value, 'required', or a cascade/append modifier",
  "parse.requiredSubFieldNoValue": "'required' sub-field cannot have a value",
  "parse.expectedCloseFieldBody": "Expected {close} to close field body",
  "parse.rosterOnModuleNotInterface": "An aspect roster ({frag}) lives on a module, not an interface",
  "parse.interfaceNoSubDecl": "Interface body cannot contain sub-declarations",
  "parse.expectedOpenOrColonAfter": "Expected {open} or ':' after '{context}'",
  "parse.inClauseBodyNestedProcess":
    "'in <Module>' is not allowed on a body-nested process — lexical placement already names the owner",

  // lexer.ts
  "parse.unexpectedChar": "Unexpected character: {ch}",
  "parse.stableIdSuffix": "Stable ID requires alphanumeric suffix after '#'",
  "parse.unterminatedString": "Unterminated string",
  "parse.unterminatedTripleString": "Unterminated triple-quoted string",

  // v010-decl-parser.ts — selector/view/policy/test/knob grammar
  "parse.policyHeaderAfterRule": "A header field must come before the first rule (VIEW_HEADER_AFTER_CLAUSE)",
  "parse.unexpectedInPolicyBody": "Unexpected token in policy body: '{tok}'",
  "parse.exceptNeedsReason": "An `except` waiver needs a reason string",
  "parse.exceptFieldBodyOnly": "An `except` field body takes `key: value` fields only",
  "parse.expectedChangeVerb":
    "Expected a change verb (`added` / `removed` / `changed`) after the `when` subject",
  "parse.gateBodyReviewOnly": "Gate body v1 takes `require review from …` entries only",
  "parse.changedGetterSigiled": "`changed(...)` takes a sigiled getter (`@field` / `@@aspect` / `@builtin`)",
  "parse.testMissingTarget": "A test targets one process — add `of <Process>` (TEST_MISSING_TARGET)",
  "parse.testDescAfterBody": "A description must precede the first `when`/`then` (TEST_HEADER_AFTER_BODY)",
  "parse.testFieldAfterBody": "A header field must precede the first `when`/`then` (TEST_HEADER_AFTER_BODY)",
  "parse.unexpectedInTestBody": "Unexpected token in test body: '{tok}'",
  "parse.expectedPinValue": "Expected a pin value",
  "parse.assertionTrailingString":
    "An assertion takes no trailing comment string (TEST_ASSERTION_TRAILING_STRING)",
  "parse.expectedCountNumber": "Expected a count number after the comparator",
  "parse.expectedDuration": "Expected a duration (e.g. `3d`, `500ms`)",
  "parse.unexpectedInStyleBody": "Unexpected token in style body: '{tok}'",
  "parse.renameNeedsLabel": "`rename` takes a label string",
  "parse.lensEntryShape": "A lens entry is `name [\"Title\"]: <expr>`",
  "parse.usePolicyBodyOnly": "A `use policy` instance body takes `except` / `escalate` only",
  "parse.viewDescBeforeClause":
    "A description string must precede the first clause — views have no positional prose",
  "parse.viewKnobBeforeClause": "A `knob` declaration must precede the first clause (VIEW_HEADER_AFTER_CLAUSE)",
  "parse.viewHeaderBeforeClause": "A header member must precede the first clause (VIEW_HEADER_AFTER_CLAUSE)",
  "parse.layoutRemoved": "`layout` is removed — layout is the solver's job; pin a position via {example}",
  "parse.includeExcludeReserved": "`{word}` is reserved — select with `show`/`hide` over selectors instead",
  "parse.oneRepresentationClause":
    "A view has at most one representation clause — `{kind}` conflicts with the earlier `{firstKind}` (board = their absence)",
  "parse.unexpectedInViewBody": "Unexpected token in view body: '{tok}'",
  "parse.onInFlowView": "`on <plane>` is meaningless in a `flow` view — a process carries its own plane (§3)",
  "parse.focusAspectSyntax": "aspects are `@@{key}:\"{val}\"` — write `show @@{key}:\"{val}\"`",
  "parse.groupByGetterSigiled": "getters are sigiled: `group by @@{key}`",
  "parse.groupByNeedsGetter": "`group by` takes a sigiled getter (`group by @@team`)",
  "parse.onNeedsPlane": "`on` needs at least one plane name",
  "parse.swimlaneRetired": "`swimlane` named the default — write `flow X` plain, or `sequence`/`bpmn`",
  "parse.flowBodyBpmnOnly": "lane/pool configure the bpmn mode — write {example}",
  "parse.untitledPoolElementRef":
    "an untitled pool takes an element ref — title a query pool (`pool \"Name\": <query>`)",
  "parse.replicateAspectPlane": "`replicate` takes an aspect plane: `replicate @@system`",
  "parse.engineAspectPlane": "`engine` takes an aspect plane: `engine @@team`",
  "parse.flowBodyEntries":
    "A flow body takes `lane …`, `pool …`, `replicate @@…`, and `engine @@…` entries",
  "parse.titledLanePoolSameLine":
    "a titled {head} takes its selector on the same line: `{head} \"Title\": <selector>`",
  "parse.laneOrPoolByGetter": "`{head}` takes a sigiled getter (`{head} @@team`)",
  "parse.sortByGetter": "`sort by` takes a sigiled getter (`sort by @name`)",
  "parse.tableBodyEntries": "A table body takes `column …` and `sort by …` entries",
  "parse.columnShape": "A column is `column @getter [\"Title\"]` or `column \"Title\": <expr>`",
  "parse.matrixBodyEntries":
    "A matrix body takes `axis`/`rows`/`cols <getter>`, `order by <strategy>`, and `cluster`",
  "parse.gridBodyEntries": "A grid body takes `rows <getter>` / `cols <getter>` / `color <getter>` only",
  "parse.indexBodyEntries": "An index body takes `sort by <getter>` and `columns <n>` entries",
  "parse.indexColumnsNumber": "`columns` takes a positive integer (`columns 3`)",
  "parse.rowsGetter": "`rows` takes a sigiled getter",
  "parse.colsGetter": "`cols` takes a sigiled getter",
  "parse.axisGetter": "`axis` takes a sigiled getter (`axis @@team`)",
  "parse.orderStrategy": "`order by` takes `cluster`, `layer`, or a sigiled getter",
  "parse.matrixClusterRectangular":
    "`order`/`cluster` need a square DSM axis — use `axis <getter>` or drop `rows`/`cols`",
  "parse.matrixAxisWithRowsCols":
    "`axis` is the square-axis form — drop `rows`/`cols` (or drop `axis` for a rectangular matrix)",
  "parse.colorGetter": "`color` takes a sigiled getter",
  "parse.knobBlockEntryShape": "A knob block entry is `<name> one of: …` or `<name> any of: …`",
  "parse.knobFromSigiled": "knob `from` takes a sigiled key (`from @@security.zone`)",
  "parse.knobShape":
    "A knob is `<name>: <default>`, `<type> <name>`, `<name> from @@key`, or a {block} block",
  "parse.knobBlockOneOrAny": "A knob block entry uses `one of` or `any of`",
  "parse.knobRestrictionVocab": "Knob restrictions are `min` / `max` / `step` (the vocabulary is frozen)",
  // Decorations
  "parse.decorationBadFieldType":
    "'{got}' is not a decoration field type — use marker, text, number, bool, ref, or `list of <type>`",
  "parse.decorationMixedArgs":
    "`@{name}` mixes a positional and named arguments — with two or more arguments, name them all",
  "parse.decorationNotAttachable":
    "`@{name}` cannot decorate a {kind} — view-layer declarations may READ a decoration in a selector, but not carry one",
  "parse.decorationOnDecoration": "A `decoration` declaration cannot itself be decorated",
  "parse.decorationOnSubField":
    "`@{name}` cannot decorate a field sub-member — decorate the top-level field instead",
};
